import captureError from '@disco/utils/captureError'
import * as React from 'react'
import { checkIfConnectError } from '@disco/utils/captureError'
import ErrorPage from 'components/error-page'
type State = {
  error: Error | null | undefined
  isConnectError: boolean
}
type ErrorDetails = {
  error: Error
  isConnectError: boolean
}
/**
 * Error boundary component that catches JavaScript errors in child components
 * Displays error pages and reports errors for monitoring
 * @param children - Child components to wrap with error boundary
 * @param renderError - Optional custom error renderer function
 * @param errorHandler - Function to handle error reporting and logging
 */
export default class ErrorBoundary extends React.Component<
  {
    children: React.ReactNode
    renderError?: (details: ErrorDetails) => React.ReactNode
    errorHandler: (details: ErrorDetails) => unknown
  },
  State
> {
  static defaultProps: {
    redirectDelay: number
  } = {
    redirectDelay: 5000,
  }
  state: State = {
    error: null,
    isConnectError: false,
  }

  componentDidCatch(error: Error, info: any) {
    // We don't want to show error page on errors related to selectors
    // throwing in redux connect due to data not loaded for some modal,
    // when user reopens browser with opened modal route.
    // It's known issue that shouldn't scare users
    const isConnectError = checkIfConnectError(error)
    this.setState({
      error,
      isConnectError,
    })

    const renderingError = new Error(error.message)
    renderingError.name = `ReactRenderingError`
    renderingError.stack = info.componentStack
    renderingError.cause = error
    captureError(renderingError)

    this.props.errorHandler({
      error,
      isConnectError,
    })
  }

  render(): null | React.ReactNode {
    const { error, isConnectError } = this.state

    if (error) {
      if (isConnectError) {
        return null
      }

      if (this.props.renderError) {
        return this.props.renderError({
          error,
          isConnectError,
        })
      }

      return (
        <ErrorPage
          title="Oops! Something went wrong"
          subtitle="Internal error"
          body={
            <span>
              DISCO has encountered an internal error and the team have been
              notified. This page will reload automatically.
            </span>
          }
        />
      )
    }

    return this.props.children
  }
}
